import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  Renderer2,
  ElementRef,
} from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { InspectionService } from '../../../inspection.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-calendar-modal',
  templateUrl: './calendar-modal.component.html',
  styleUrls: ['./calendar-modal.component.scss'],
})
export class CalendarModalComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('calendarModal', { static: true }) public calendarModal!: ModalDirective;
  @ViewChild('calendar') calendar!: MatCalendar<Date>;

  @Input() inspectionId: any;
  @Input() selectedDateType: string = '';
  @Input() selectedDateInput: Date | string | null = null;

  @Output() readonly selectedDate = new EventEmitter<Date>();
  @Output() readonly cancel = new EventEmitter<void>();

  selected: Date = new Date();
  selectedTabIndex: number = 0;

  private readonly destroy$ = new Subject<void>();
  private readonly hideLabelsDelay = 100;
  private readonly quickSelectDays = [30, 60, 90] as const;

  constructor(
    public readonly inspectionService: InspectionService,
    private readonly cdr: ChangeDetectorRef,
    private readonly renderer: Renderer2,
    private readonly el: ElementRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDateInput']) {
      this.updateSelectedDate();
    }
  }

  ngAfterViewInit(): void {
    this.setupModalSubscriptions();
    this.scheduleHideMonthLabels();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openModal(): void {
    this.updateSelectedDate();
    this.calendarModal.show();
    this.scheduleHideMonthLabels();
    this.cdr.detectChanges();
  }

  setDays(days: number): void {
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + days);
    this.updateSelection(targetDate);
  }

  onTabChange(days: number): void {
    if (days) {
      this.setDays(days);
    }
  }

  onSave(): void {
    this.selectedDate.emit(new Date(this.selected));
    this.closeModal();
  }

  onCancel(): void {
    this.cancel.emit();
    this.closeModal();
  }

  private setupModalSubscriptions(): void {
    this.calendarModal.onHidden.pipe(takeUntil(this.destroy$)).subscribe((event: any) => {
      if (this.isDismissedByUserAction(event)) {
        this.onCancel();
      }
    });

    this.calendarModal.onShow.pipe(takeUntil(this.destroy$)).subscribe((): void => {
      this.updateSelectedDate();
      this.scheduleHideMonthLabels();
    });
  }

  private updateSelectedDate(): void {
    this.selected = this.parseInputDate(this.selectedDateInput);
    this.updateTabSelection(this.selected);
    if (this.calendar) {
      this.calendar.activeDate = new Date(this.selected);
      this.calendar.selected = new Date(this.selected);
      this.cdr.detectChanges();
    }
  }

  private updateTabSelection(targetDate: Date): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);

    const daysDiff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff <= 30) {
      this.selectedTabIndex = 0; // 30 days tab
    } else if (daysDiff <= 60) {
      this.selectedTabIndex = 1; // 60 days tab
    } else {
      this.selectedTabIndex = 2; // 90 days tab
    }
  }

  onDateSelected(date: Date): void {
    this.selected = new Date(date);
    if (this.calendar) {
      this.calendar.activeDate = new Date(date);
      this.calendar.selected = new Date(date);
      this.cdr.detectChanges();
    }
    this.scheduleHideMonthLabels();
  }

  private parseInputDate(input: Date | string | null): Date {
    if (!input) {
      const today = new Date();
      today.setDate(today.getDate() + 30);
      return today;
    }

    const parsedDate = input instanceof Date ? input : new Date(input);

    if (!this.isValidDate(parsedDate)) {
      return new Date();
    }
    return parsedDate;
  }

  private isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  private updateSelection(date: Date): void {
    this.selected = new Date(date);
    if (this.calendar) {
      this.calendar.activeDate = new Date(date);
      this.calendar.selected = new Date(date);
      this.cdr.detectChanges();
    }
    this.scheduleHideMonthLabels();
  }

  private isDismissedByUserAction(event: any): boolean {
    return event?.dismissReason === 'esc' || event?.dismissReason === 'backdrop-click';
  }

  private closeModal(): void {
    this.calendarModal.hide();
  }

  private scheduleHideMonthLabels(): void {
    setTimeout(() => this.hideMonthLabelsInDateRows(), this.hideLabelsDelay);
  }

  private hideMonthLabelsInDateRows(): void {
    const calendarBody = this.el.nativeElement.querySelector('.mat-calendar-body');
    if (!calendarBody) return;

    const rows = calendarBody.querySelectorAll('tr');
    rows.forEach((row: Element) => {
      const hasDateCells = row.querySelectorAll('.mat-calendar-body-cell-container').length > 0;
      const monthLabel = row.querySelector('.mat-calendar-body-label') as HTMLElement;

      if (hasDateCells && monthLabel) {
        this.renderer.setStyle(monthLabel, 'visibility', 'hidden');
      }
    });
  }
}
